import { roMembraneProducts, roMembraneProductsList } from './roMembraneProducts.js';
import { ufMembraneProducts, ufMembraneProductsList } from './ufMembraneProducts.js';
import { mbrMembraneProducts, mbrMembraneProductsList } from './mbrMembraneProducts.js';
import { pressureVesselProducts, pressureVesselProductsList } from './pressureVesselProducts.js';
import { mfFilterCartridgeProducts, mfFilterCartridgeProductsList } from './mfFilterCartridgeProducts.js';
import { carbonFilterCartridgeProducts, carbonFilterCartridgeProductsList } from './carbonFilterCartridgeProducts.js';
import { liquidFilterBagProducts, liquidFilterBagProductsList } from './liquidFilterBagProducts.js';
import { filterHousingProducts, filterHousingProductsList } from './filterHousingProducts.js';
import { multiMediaFilterProducts, multiMediaFilterProductsList } from './multiMediaFilterProducts.js';
import { waterTreatmentSystemProducts, waterTreatmentSystemProductsList } from './waterTreatmentSystemProducts.js';
import { householdPurifierProducts, householdPurifierProductsList } from './householdPurifierProducts.js';
import { uvSterilizerProducts, uvSterilizerProductsList } from './uvSterilizerProducts.js';
import { otherSparePartsProducts, otherSparePartsProductsList } from './otherSparePartsProducts.js';
import { productCategories } from './productCategories.js';

// Convert different product formats to a unified structure
const convertToProductData = (products) => {
  const productData = {};
  products.forEach(product => {
    // Handle both direct product objects and nested structures
    if (product.products) {
      // Handle pressure vessel format
      product.products.forEach(subProduct => {
        productData[subProduct.id] = {
          ...subProduct,
          category: product.category || subProduct.category,
          image: product.image || subProduct.image
        };
      });
    } else {
      // Handle direct product format
      productData[product.id] = product;
    }
  });
  return productData;
};

const convertToProductList = (products) => {
  return products.map(product => ({
    id: product.id,
    name: product.name || product.title, // Use title as fallback for name
    shortDescription: product.shortDescription || product.description || '',
    image: product.image,
    category: product.category,
    features: product.features || [],
    applications: product.applications || [],
  }));
};

// Create unified product data object (for ProductDetail.jsx)
export const productData = {
  // Use the detailed product objects directly
  ...roMembraneProducts,
  ...ufMembraneProducts,
  ...mbrMembraneProducts,
  ...mfFilterCartridgeProducts,
  ...carbonFilterCartridgeProducts,
  ...liquidFilterBagProducts,
  ...filterHousingProducts,
  ...multiMediaFilterProducts,
  ...waterTreatmentSystemProducts,
  ...householdPurifierProducts,
  ...uvSterilizerProducts,
  ...otherSparePartsProducts,
  // Convert pressure vessel arrays to object format
  ...convertToProductData(pressureVesselProducts),
};

// Get all products as a list (for Products.jsx)
export const getAllProducts = () => {
  return [
    ...convertToProductList(roMembraneProductsList),
    ...convertToProductList(ufMembraneProductsList),
    ...convertToProductList(mbrMembraneProductsList),
    ...convertToProductList(pressureVesselProductsList),
    ...convertToProductList(mfFilterCartridgeProductsList),
    ...convertToProductList(carbonFilterCartridgeProductsList),
    ...convertToProductList(liquidFilterBagProductsList),
    ...convertToProductList(filterHousingProductsList),
    ...convertToProductList(multiMediaFilterProductsList),
    ...convertToProductList(waterTreatmentSystemProductsList),
    ...convertToProductList(householdPurifierProductsList),
    ...convertToProductList(uvSterilizerProductsList),
    ...convertToProductList(otherSparePartsProductsList),
  ];
};

// Get products by category
export const getProductsByCategory = (category) => {
  const allProducts = getAllProducts();
  return allProducts.filter(product => product.category === category);
};

// Get product by ID
export const getProductById = (id) => {
  return productData[id] || null;
};

// Get all product categories
export const getProductCategories = () => {
  return productCategories;
};

// Get category by products
export const getCategoriesWithCounts = () => {
  const allProducts = getAllProducts();
  const categoryCounts = {};
  
  allProducts.forEach(product => {
    const category = product.category;
    categoryCounts[category] = (categoryCounts[category] || 0) + 1;
  });

  return productCategories.map(category => ({
    ...category,
    count: categoryCounts[category.name] || 0,
  }));
};

export default {
  productData,
  getAllProducts,
  getProductsByCategory,
  getProductById,
  getProductCategories,
  getCategoriesWithCounts,
};